import { useState, useRef, useCallback } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import listPlugin from '@fullcalendar/list';
import interactionPlugin from '@fullcalendar/interaction';
import { User, Calendar, Edit2 } from '../../icons/wp-icons';
import { getKalenderAbwesenheiten } from '../../api/abwesenheitApi';
import { useAuth } from '../../context/AuthContext';
import Modal from '../Common/Modal';

const TYP_LABELS = {
  URLAUB: 'Urlaub',
  KRANKHEIT: 'Krankheit',
  FORTBILDUNG: 'Fortbildung',
  HOMEOFFICE: 'Homeoffice',
  SONSTIGES: 'Sonstiges'
};

const STATUS_LABELS = {
  BEANTRAGT: { label: 'Beantragt', color: 'bg-yellow-100 text-yellow-700' },
  GENEHMIGT: { label: 'Genehmigt', color: 'bg-green-100 text-green-700' },
  ABGELEHNT: { label: 'Abgelehnt', color: 'bg-red-100 text-red-700' },
  STORNIERT: { label: 'Storniert', color: 'bg-gray-100 text-gray-500' }
};

function AbwesenheitKalender({ onEdit }) {
  const { user } = useAuth();
  const calendarRef = useRef(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  const loadEvents = useCallback(async (start, end) => {
    try {
      setLoading(true);
      const data = await getKalenderAbwesenheiten(start.toISOString(), end.toISOString());
      setEvents(data);
    } catch (error) {
      console.error('Fehler beim Laden der Abwesenheiten:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleDatesSet = (dateInfo) => {
    // Erweitere den Range für Buffer
    const start = new Date(dateInfo.start);
    start.setMonth(start.getMonth() - 1);
    const end = new Date(dateInfo.end);
    end.setMonth(end.getMonth() + 1);
    loadEvents(start, end);
  };

  const handleEventClick = (clickInfo) => {
    const props = clickInfo.event.extendedProps;
    setSelectedEvent({
      id: clickInfo.event.id,
      title: clickInfo.event.title,
      start: clickInfo.event.start,
      end: clickInfo.event.end,
      ...props
    });
    setIsDetailModalOpen(true);
  };

  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('de-DE', {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  const canEdit = selectedEvent && (
    user?.rolle === 'admin' ||
    (selectedEvent.benutzerId === user?.id && selectedEvent.status === 'BEANTRAGT')
  );

  return (
    <div className="space-y-4">
      <style>{`
        .fc-event {
          cursor: pointer;
          transition: filter 0.15s ease;
        }
        .fc-event:hover {
          filter: brightness(1.1);
        }
        .fc .fc-daygrid-event {
          border-radius: 4px;
          padding: 2px 4px;
        }
        @media (max-width: 767px) {
          .fc .fc-toolbar {
            flex-direction: column;
            gap: 8px;
          }
          .fc .fc-toolbar-chunk {
            display: flex;
            justify-content: center;
          }
          .fc .fc-toolbar-title {
            font-size: 1.1rem !important;
          }
          .fc .fc-button {
            padding: 6px 10px !important;
            font-size: 0.75rem !important;
          }
        }
      `}</style>

      <div className="bg-surface rounded-card shadow-card p-4 sm:p-6 relative">
        {loading && (
          <div className="absolute inset-0 bg-white/70 z-10 flex items-center justify-center rounded-card">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        )}

        <FullCalendar
          ref={calendarRef}
          plugins={[dayGridPlugin, listPlugin, interactionPlugin]}
          initialView="dayGridMonth"
          headerToolbar={{
            left: 'prev,next today',
            center: 'title',
            right: 'dayGridMonth,listMonth'
          }}
          locale="de"
          firstDay={1}
          events={events}
          eventClick={handleEventClick}
          datesSet={handleDatesSet}
          height="auto"
          eventDisplay="block"
          dayMaxEvents={3}
          moreLinkText={(num) => `+${num} mehr`}
          buttonText={{
            today: 'Heute',
            month: 'Monat',
            list: 'Liste'
          }}
          noEventsText="Keine Abwesenheiten"
        />
      </div>

      {/* Legende */}
      <div className="bg-surface rounded-card shadow-card p-4">
        <h3 className="text-sm font-medium text-text-muted mb-3">Legende</h3>
        <div className="flex flex-wrap gap-4">
          <div className="flex items-center gap-2">
            <span className="w-4 h-4 rounded" style={{ backgroundColor: '#10b981' }}></span>
            <span className="text-sm">Urlaub</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-4 h-4 rounded" style={{ backgroundColor: '#ef4444' }}></span>
            <span className="text-sm">Krankheit</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-4 h-4 rounded" style={{ backgroundColor: '#3b82f6' }}></span>
            <span className="text-sm">Fortbildung</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-4 h-4 rounded" style={{ backgroundColor: '#f59e0b' }}></span>
            <span className="text-sm">Homeoffice</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-4 h-4 rounded" style={{ backgroundColor: '#6b7280' }}></span>
            <span className="text-sm">Sonstiges</span>
          </div>
        </div>
      </div>

      {/* Detail Modal */}
      <Modal
        isOpen={isDetailModalOpen}
        onClose={() => {
          setIsDetailModalOpen(false);
          setSelectedEvent(null);
        }}
        title="Abwesenheit Details"
      >
        {selectedEvent && (
          <div className="space-y-4">
            {/* Mitarbeiter */}
            <div className="flex items-center gap-3">
              <User className="w-5 h-5 text-text-muted" />
              <span className="font-medium">{selectedEvent.benutzerName}</span>
            </div>

            {/* Typ & Status */}
            <div className="flex items-center gap-3 flex-wrap">
              <span
                className="px-3 py-1 rounded-full text-sm font-medium"
                style={{ backgroundColor: selectedEvent.backgroundColor + '20', color: selectedEvent.backgroundColor }}
              >
                {TYP_LABELS[selectedEvent.abwesenheitTyp] || selectedEvent.abwesenheitTyp}
              </span>
              {selectedEvent.status && (
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${STATUS_LABELS[selectedEvent.status]?.color || ''}`}>
                  {STATUS_LABELS[selectedEvent.status]?.label || selectedEvent.status}
                </span>
              )}
            </div>

            {/* Zeitraum */}
            <div className="flex items-start gap-3">
              <Calendar className="w-5 h-5 text-text-muted mt-0.5" />
              <div>
                <p>{formatDate(selectedEvent.start)}</p>
                {selectedEvent.end && selectedEvent.start?.toDateString() !== new Date(new Date(selectedEvent.end).getTime() - 86400000).toDateString() && (
                  <p className="text-sm text-text-muted">
                    bis {formatDate(new Date(new Date(selectedEvent.end).getTime() - 86400000))}
                  </p>
                )}
              </div>
            </div>

            {/* Grund */}
            {selectedEvent.grund && (
              <div className="pt-3 border-t border-border">
                <p className="text-sm text-text-muted mb-1">Grund</p>
                <p>{selectedEvent.grund}</p>
              </div>
            )}

            {/* Aktionen */}
            <div className="flex gap-3 pt-4 border-t border-border">
              {canEdit && (
                <button
                  onClick={() => {
                    setIsDetailModalOpen(false);
                    // Wir brauchen die vollständigen Daten
                    onEdit({
                      id: selectedEvent.id,
                      typ: selectedEvent.abwesenheitTyp,
                      status: selectedEvent.status,
                      startDatum: selectedEvent.start?.toISOString(),
                      endDatum: selectedEvent.end ? new Date(new Date(selectedEvent.end).getTime() - 86400000).toISOString() : selectedEvent.start?.toISOString(),
                      ganztags: true,
                      grund: selectedEvent.grund,
                      benutzerId: selectedEvent.benutzerId
                    });
                  }}
                  className="flex items-center justify-center gap-2 flex-1 bg-primary text-white py-2 rounded-button font-medium hover:bg-primary-dark transition"
                >
                  <Edit2 className="w-4 h-4" />
                  Bearbeiten
                </button>
              )}
              <button
                onClick={() => {
                  setIsDetailModalOpen(false);
                  setSelectedEvent(null);
                }}
                className="flex-1 bg-gray-200 text-text py-2 rounded-button font-medium hover:bg-gray-300 transition"
              >
                Schließen
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

export default AbwesenheitKalender;
